import { type FC } from 'react'
import NextImage, { getImageProps } from 'next/image'
import type { TypeCmsMediumImageVariant, TypeCmsModernImage, TypeCmsRasterImage, TypeCmsVectorImage } from '@/schema'

// KEEP STRING ORDER FROM LARGEST TO SMALLEST
const VARIANTS_IN_PROJECT = [
  'xl', 'l', 'm', 's',
  'sliderXl', 'sliderL', 'sliderM', 'sliderS'
]

type TypeVariantsKeys = typeof VARIANTS_IN_PROJECT[number]

type Props = {
  readonly media?: TypeCmsRasterImage | TypeCmsVectorImage,
  readonly lazy?: boolean,
  readonly initialInView?: boolean,
  onClick?: () => void,
  onLoad?: () => void,
  readonly fill?: boolean,
  readonly outRef?: (node?: Element) => void,
  readonly className?: string,
  readonly sizes?: string,
  readonly quality?: number
  readonly ariaHidden?: boolean
  readonly unoptimized?: boolean
}

export const getMediaVariant = (
  obj: Record<string, TypeCmsMediumImageVariant> | undefined,
  variantNames: TypeVariantsKeys[] = VARIANTS_IN_PROJECT
): TypeCmsMediumImageVariant | undefined => {
  if (!obj) return undefined
  for (const key of variantNames) {
    if (key in obj) return obj[key]
  }
  return undefined
}

const Picture = ({ media, lazy, quality, ariaHidden }) => {
  const common = { alt: media.alt, sizes: '100vw' }
  const mobileVariant = getMediaVariant(media, [...VARIANTS_IN_PROJECT].reverse())
  const desktopVariant = getMediaVariant(media, VARIANTS_IN_PROJECT)

  const { props:mobileProps } = getImageProps({
    ...common,
    ...mobileVariant.size,
    quality,
    src: mobileVariant.src,
  })

  const { props:desktopProps } = getImageProps({
    ...common,
    ...desktopVariant.size,
    quality,
    src: desktopVariant.src,
  })

  const srcAvailable = desktopProps.src || mobileProps.src

  return (
    <>
      {srcAvailable ?
        <picture>
          <source media='(min-width: 768px)' srcSet={desktopProps.src} height={desktopProps.height} width={desktopProps.width} />
          <source media='(max-width: 767px)' srcSet={mobileProps.src} height={mobileProps.height} width={mobileProps.width} />
          <img loading={lazy ? 'lazy' : 'eager'} draggable='false' {...ariaHidden && { 'aria-hidden': ariaHidden }} alt='img'/>
        </picture>
        :
        <div className='placeholder' />
      }
    </>
  )
}

// function isTypeVectorImage(media: TypeCmsVectorImage | TypeCmsRasterImage): media is TypeVectorImage {
//   return media?.type === 'svg'
// }
function isTypeVectorImage(
  media: TypeCmsRasterImage | TypeCmsVectorImage
): media is TypeCmsVectorImage {
  return media && 'type' in media && media.type === 'svg'
}

const Image = ({ media, lazy, fill, quality, ariaHidden, sizes, unoptimized }) => {
  const isVector = isTypeVectorImage(media)
  const variant = isVector ? media : getMediaVariant(media, VARIANTS_IN_PROJECT)
  return (
    <>
      {variant?.src ?
        <NextImage src={variant.src} priority={!lazy} loading={lazy ? 'lazy' : 'eager'} {...quality !== 75 && { quality }}
          alt={media.alt}
          {...!fill && {
            height: variant.size.height,
            width: variant.size.width
          }}
          {...sizes && { sizes }}
          {...ariaHidden && { 'aria-hidden': ariaHidden }}
          {...(unoptimized || isVector) && { unoptimized: true }}
        />
        :
        <div className='placeholder' />
      }
    </>
  )
}

const ComponentByType = {
  'single': Image,
  'multi': Picture
}

const multiVariants = (obj: TypeCmsModernImage | undefined, keys: TypeVariantsKeys[] ): boolean => {
  if(!obj) return undefined
  // Count how many keys in the array are present in the object
  const matchedKeys = keys.filter(keyname => keyname in obj)
  // Return true if at least two keys are found
  return matchedKeys.length >= 2
}

export const Img: FC<Props> = ({ media, lazy, quality = 75, fill = false, sizes, ariaHidden = false, unoptimized = false}) => {
  const Component = ComponentByType[multiVariants(media as TypeCmsModernImage, VARIANTS_IN_PROJECT) ? 'multi' : 'single']
  return media ? <Component media={media} lazy={lazy} quality={quality} fill={fill} sizes={sizes} ariaHidden={ariaHidden} unoptimized={unoptimized} /> : <div className='placeholder' />
}
